// The DOM laid over the room: the bar, the hover label, the cards that open
// beside a selected device or game, the play panel, toasts and
// the sign-in gate. It renders from StudioState and calls back with intents.

import type { StudioCore, StudioState } from "../core/studio.ts";
import type { AppInfo, DeviceId, NativeTarget, PackageInfo, UserInfo } from "../core/types.ts";
import { CUSTOM_SKIN } from "../core/catalog.ts";
import { EXTRA_DEVICE_COLOURS_VISIBLE, MEMBERSHIP_SIGNUPS_OPEN } from "../core/membership.ts";
import { formatBytes, formatList } from "../core/i18n.ts";
import { LEGAL } from "../core/legal.ts";
import { HELP_PATH } from "../core/install-guide.ts";
import { createPalette } from "./palette.ts";
import { openAccountMenu, openPaywall, openSignIn, type AccountActions, type PaywallReason } from "./account.ts";
import { createSignIn } from "./signin.ts";
import type { Plan } from "../account/contract.ts";
import type { AuthProvidersResponse } from "../core/protocol.ts";
import { track } from "../api/events.ts";
import { h, MARK, svg } from "./dom.ts";
import { lang, localized, t } from "./lang.ts";
import { appTagline, deviceName, phrase, rich, skinLabel, skinName } from "./names.ts";

/** The arrow into a tray: a download. */
const DOWNLOAD = `<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M10 3v9.5M5.8 8.600 10 12.800l4.200-4.200M4 16.500h12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

/** How long a toast stays: a line, and a line with a button to reach. */
export const TOAST_MS = 3200;
export const TOAST_ACTION_MS = 9000;

/** A toast's button: what it says and what it does. */
export interface ToastAction {
  label: string;
  run: () => void;
}

/**
 * The button of a toast. It runs the action and closes the toast. Its keys
 * stay on it, so the room's keys (Enter for a game, Escape for back) do not
 * take its press; Escape closes the toast.
 */
export function toastButton(action: ToastAction, close: () => void): HTMLButtonElement {
  const button = h("button", { class: "toast-act", type: "button", text: action.label, onclick: () => {
    close();
    action.run();
  } });
  button.addEventListener("keydown", (event) => {
    event.stopPropagation();
    if (event.key === "Escape") close();
  });
  button.addEventListener("keyup", (event) => event.stopPropagation());
  return button;
}

export interface OverlayActions {
  /** Turn the rack of games to the page before or after. */
  turnShelf(by: -1 | 1): void;
  selectDevice(device: DeviceId): void;
  selectApp(app: string): void;
  openLaptop(): void;
  openCloset(): void;
  openSettings(): void;
  openTv(): void;
  /** Change the game on the television: -1 the one before, 1 the next. */
  tvTurn(by: -1 | 1): void;
  /** Open the game the television is showing. */
  tvChoose(): void;
  tvMuted(): boolean;
  tvMute(muted: boolean): void;
  /** Download a game as a device's native build. */
  native(app: string, target: NativeTarget): void;
  /** The account's own calls: its state, the payment page, the plan page, signing out. */
  account: AccountActions;
  back(): void;
  play(app: string, device?: DeviceId): void;
  /** Give one of the user's own games another title. Resolves with a refusal to show, or null. */
  retitle(app: string, title: string): Promise<string | null>;
  /** Wear a catalog skin, or the account's own `color` under CUSTOM_SKIN. */
  skin(device: DeviceId, skin: string, color?: string): void;
  /** Show `color` on the device without keeping it, while it is being mixed. */
  previewColor(device: DeviceId, color: string): void;
  publish(app: string, on: boolean): void;
  /** Let members download one of the user's own games, or stop it. */
  allowNative(app: string, on: boolean): void;
  /** Put another author's game on the shelf, or take a kept one off. */
  keep(app: string, on: boolean): void;
  /** Move one of the user's own games to another address label. Resolves with a refusal to show, or null. */
  rename(app: string, slug: string): Promise<string | null>;
  /** Delete one of the user's own games for good. */
  remove(app: string): void;
  signOut(): void;
}

export class Overlay {
  private bar: HTMLElement;
  private backButton: HTMLButtonElement;
  private who: HTMLElement;
  private label: HTMLElement;
  private card: HTMLElement;
  private toasts: HTMLElement;
  private hint: HTMLElement;
  private index: HTMLElement;
  private pager: HTMLElement;
  private pagerBack: HTMLButtonElement;
  private pagerOn: HTMLButtonElement;
  private pagerCount: HTMLElement;
  private indexKey = "";
  private toastTimer = 0;
  /** The device whose palette is open. */
  private mixing: DeviceId | null = null;
  /** The game whose card is asking whether to delete it. */
  private removing: string | null = null;
  /** The game whose card shows its title as a field. */
  private renaming: string | null = null;
  private avatar: HTMLButtonElement | null = null;

  constructor(private root: HTMLElement, private core: () => StudioCore | null, readonly actions: OverlayActions) {
    this.backButton = h("button", { class: "pill back", type: "button", onclick: () => actions.back() },
      svg(`<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M7.5 2 3.5 6l4 4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`),
      t("room.back"));
    this.who = h("div", { class: "who" });
    this.bar = h("header", { class: "bar" },
      h("a", { class: "pill brand", href: localized("/"), "aria-label": t("room.home") }, svg(MARK), h("span", { text: "Pocket Studio" })),
      this.backButton,
      this.who);
    this.label = h("div", { class: "hover-label", "aria-hidden": "true" });
    this.card = h("aside", { class: "card", "aria-live": "polite" });
    this.toasts = h("div", { class: "toast", role: "status" });
    this.toasts.addEventListener("pointerenter", () => this.holdToast(true));
    this.toasts.addEventListener("pointerleave", () => this.holdToast(this.toasts.contains(document.activeElement)));
    this.toasts.addEventListener("focusin", () => this.holdToast(true));
    this.toasts.addEventListener("focusout", () => this.holdToast(this.toasts.matches(":hover")));
    this.hint = h("p", { class: "hint", text: t("room.hint") });
    this.index = h("nav", { class: "index", "aria-label": t("room.index") });
    // The rack's pages: the view keeps this on the rack and says which page it shows.
    this.pagerBack = h("button", { type: "button", "aria-label": t("room.shelf.previous"), onclick: () => actions.turnShelf(-1) }, svg(`<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M7.5 2 3.5 6l4 4" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>`));
    this.pagerOn = h("button", { type: "button", "aria-label": t("room.shelf.next"), onclick: () => actions.turnShelf(1) }, svg(`<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M4.5 2l4 4-4 4" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>`));
    this.pagerCount = h("span", { "aria-live": "polite" });
    this.pager = h("nav", { class: "pager", "aria-label": t("room.shelf.pages") }, this.pagerBack, this.pagerCount, this.pagerOn);
    this.pager.hidden = true;
    root.append(this.bar, this.index, this.label, this.pager, this.card, this.hint, this.toasts);
    this.card.hidden = true;
    this.backButton.hidden = true;
  }

  setUser(user: UserInfo | null): void {
    this.who.replaceChildren();
    if (!user) return;
    const initial = (user.name || user.handle || "?").trim()[0]!.toUpperCase();
    this.who.append(
      h("a", { class: "pill link", href: localized("/docs/mcp/"), target: "_blank", rel: "noopener", text: t("foot.mcp") }),
      h("a", { class: "pill link", href: localized("/docs/"), target: "_blank", rel: "noopener", text: t("foot.docs") }),
      h("button", { class: "pill", type: "button", onclick: () => this.actions.openCloset() },
        svg(`<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M10 6.2V5a2 2 0 1 0-2-2" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><path d="M10 6.2 2.6 12.3c-1 .9-.4 2.5 1 2.5h12.8c1.400 0 2-1.600 1-2.500z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/></svg>`),
        t("room.closet")),
      h("button", { class: "pill", type: "button", onclick: () => this.actions.openSettings() },
        svg(`<svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="2.6" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M10 2.2v2.300M10 15.500v2.300M2.200 10h2.300M15.500 10h2.300M4.500 4.500l1.600 1.600M13.900 13.900l1.600 1.600M4.500 15.500l1.600-1.600M13.900 6.100l1.600-1.600" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>`),
        t("room.settings")),
      this.avatar = h("button", {
        class: "avatar", type: "button", text: initial, title: t("room.account"), "aria-label": t("room.account"), "aria-haspopup": "dialog",
        onclick: () => {
          const core = this.core();
          if (core) openAccountMenu(this.root, this.avatar!, this.actions.account, core.state.snapshot.user, core.state.snapshot.membership, () => this.paywall("member"));
        },
      }),
    );
  }

  /** The name under the pointer. Coordinates are CSS pixels; null hides it. */
  hover(text: string | null, x = 0, y = 0): void {
    if (!text) {
      this.label.classList.remove("on");
      return;
    }
    this.label.textContent = text;
    this.label.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px) translate(-50%, -100%)`;
    this.label.classList.add("on");
  }

  /** Which page of games the rack shows, of how many. One page needs no pager. */
  shelf(page: number, pages: number): void {
    this.pager.hidden = pages < 2;
    this.pagerCount.textContent = `${page + 1} / ${pages}`;
    this.pagerCount.setAttribute("aria-label", t("room.shelf.page", { page: page + 1, pages }));
    this.pagerBack.disabled = page <= 0;
    this.pagerOn.disabled = page >= pages - 1;
  }

  /**
   * Where on the page the rack's lower edge is now, or null while it is out
   * of sight. The pager stays inside the window and clear of an open card:
   * beside a card that leaves room, above one that spans the window.
   */
  shelfAt(at: { x: number; y: number } | null): void {
    this.pager.classList.toggle("off", !at);
    if (!at || this.pager.hidden) return;
    const w = this.pager.offsetWidth / 2 + 8;
    const h = this.pager.offsetHeight / 2 + 8;
    let x = Math.min(innerWidth - w, Math.max(w, at.x));
    let y = Math.min(innerHeight - h, Math.max(h + 64, at.y));
    if (!this.card.hidden) {
      const card = this.card.getBoundingClientRect();
      if (x + w > card.left && x - w < card.right && y + h > card.top && y - h < card.bottom) {
        if (card.left >= w * 2) x = card.left - w;
        else y = card.top - h;
      }
    }
    this.pager.style.transform = `translate(${Math.round(x)}px,${Math.round(y)}px) translate(-50%,-50%)`;
  }

  /**
   * A line at the foot of the window that leaves by itself. With an action it
   * carries a button and stays longer, and it stays while the pointer is on it
   * or the button has keyboard focus.
   */
  toast(message: string, action?: ToastAction): void {
    clearTimeout(this.toastTimer);
    this.toasts.replaceChildren(message);
    this.toasts.classList.toggle("act", !!action);
    if (action) this.toasts.append(toastButton(action, () => this.endToast()));
    this.toasts.classList.add("on");
    this.holdToast(false);
  }

  /** Keep the toast on screen (`held`), or let it leave after its time from now. */
  private holdToast(held: boolean): void {
    clearTimeout(this.toastTimer);
    if (held || !this.toasts.classList.contains("on")) return;
    const time = this.toasts.classList.contains("act") ? TOAST_ACTION_MS : TOAST_MS;
    this.toastTimer = window.setTimeout(() => this.endToast(), time);
  }

  private endToast(): void {
    clearTimeout(this.toastTimer);
    this.toasts.classList.remove("on");
    // A toast that left takes its button with it, so the keyboard cannot reach a hidden one.
    this.toasts.querySelector("button")?.remove();
  }

  render(state: StudioState): void {
    const core = this.core();
    if (!core) return;
    const focus = state.focus;
    this.list(state);
    this.backButton.hidden = focus.kind === "room";
    this.root.dataset.focus = focus.kind;
    // A palette closes with the card it was opened on.
    if (focus.kind !== "closet" && !(focus.kind === "device" && focus.device === this.mixing)) this.mixing = null;
    // So does the question whether to delete a game, and a title being changed.
    if (!(focus.kind === "app" && focus.app === this.removing)) this.removing = null;
    if (!(focus.kind === "app" && focus.app === this.renaming)) this.renaming = null;
    // The hint is for a first look at the room; it leaves at the first choice.
    if (focus.kind !== "room") this.hint.remove();
    else this.hint.classList.add("on");
    if (focus.kind === "device") this.deviceCard(core, focus.device);
    else if (focus.kind === "app") this.appCard(core, focus.app);
    else if (focus.kind === "play") this.playCard(core, focus.device, focus.app);
    else if (focus.kind === "closet") this.closetCard(core);
    else if (focus.kind === "tv") this.tvCard();
    else if (focus.kind === "laptop") this.laptopCard();
    else {
      this.card.hidden = true;
      this.card.replaceChildren();
    }
  }

  /** The room as a list of buttons: off screen until it takes keyboard focus. */
  private list(state: StudioState): void {
    const key = state.snapshot.devices.map((d) => d.id).join() + "|" + state.snapshot.apps.map((a) => a.id + a.title).join();
    if (key === this.indexKey) return;
    this.indexKey = key;
    this.index.replaceChildren(
      ...state.snapshot.devices.map((device) =>
        h("button", { type: "button", text: deviceName(device.id), onclick: () => this.actions.selectDevice(device.id) })),
      ...state.snapshot.apps.map((app) =>
        h("button", { type: "button", text: app.title, onclick: () => this.actions.selectApp(app.id) })),
      h("button", { type: "button", text: t("room.laptop"), onclick: () => this.actions.openLaptop() }),
      h("button", { type: "button", text: "Pocket TV", onclick: () => this.actions.openTv() }),
    );
  }

  private open(kind: string, ...children: Array<Node | null>): void {
    this.card.replaceChildren(...children.filter((child): child is Node => child !== null));
    this.card.dataset.kind = kind;
    this.card.hidden = false;
  }

  private cover(app: AppInfo): HTMLElement {
    const thumb = h("span", { class: "thumb", style: `--a:${app.colors[0]};--b:${app.colors[1]}` });
    if (app.shot) thumb.style.backgroundImage = `url("${app.shot}")`;
    else {
      thumb.style.background = app.colors[0];
      thumb.textContent = (app.title.trim()[0] ?? "?").toUpperCase();
    }
    return thumb;
  }

  /**
   * One line of a list: the main action across the row, and beside it a
   * button that downloads this game as this device's native build. A row is
   * one game on one device, so the download needs no menu.
   */
  private row(app: AppInfo, device: DeviceId, main: HTMLElement): HTMLElement {
    // Only a game whose author uploaded a package for this device has one to offer.
    if (!app.packages.some((entry) => entry.target === device)) return h("div", { class: "row" }, main);
    const label = t("room.download", { title: app.title, device: deviceName(device) });
    return h("div", { class: "row" }, main,
      h("button", { class: "get", type: "button", title: label, "aria-label": label, onclick: () => this.actions.native(app.id, device) },
        svg(DOWNLOAD)));
  }

  /**
   * A package as a row of its own: what it is for, the file, its version and
   * its size, and the whole row downloads it. This is the row of a package
   * with no device to play it on beside it: every package of a game built
   * outside Pocket Studio, and a package for a device the game has no build
   * for.
   */
  private packageRow(app: AppInfo, entry: PackageInfo): HTMLElement {
    const label = t("room.download", { title: app.title, device: deviceName(entry.target) });
    return h("div", { class: "row" },
      h("button", { class: "row-main", type: "button", title: label, "aria-label": label, onclick: () => this.actions.native(app.id, entry.target) },
        h("span", { class: "row-text" },
          h("b", { text: t("room.package.for", { device: deviceName(entry.target) }) }),
          // A package the Studio makes has no size until it is first made.
          h("small", { text: [entry.filename, entry.version, entry.size > 0 ? formatBytes(lang(), entry.size) : ""].filter(Boolean).join(", ") })),
        h("span", { class: "go get-go", "aria-hidden": "true" }, svg(DOWNLOAD))));
  }

  /**
   * The paywall: what the membership gives, the two plans, and the way on to
   * payment. `reason` is the thing the visitor reached for; `plan` preselects one.
   */
  paywall(reason: PaywallReason, plan?: Plan): void {
    const core = this.core();
    if (core) openPaywall(this.root, this.actions.account, core.state.snapshot.membership, reason, plan);
  }

  /**
   * A device's colours: the catalog's, then the account's own. The last
   * swatch opens the palette for a member; for anyone else it and the member
   * colours carry a lock, and choosing one says what the membership gives.
   */
  private swatches(core: StudioCore, id: DeviceId): HTMLElement {
    const device = core.device(id);
    const member = core.state.snapshot.membership.active;
    const worn = device.skins.find((skin) => skin.id === device.skin);
    const own = device.skin === CUSTOM_SKIN;
    const open = EXTRA_DEVICE_COLOURS_VISIBLE && member && this.mixing === id;
    const row = h("div", { class: "swatches", role: "group", "aria-label": t("room.colour.group", { device: deviceName(id) }) },
      ...device.skins.filter((skin) => EXTRA_DEVICE_COLOURS_VISIBLE || skin.tier === "base").map((skin) =>
        h("button", {
          class: "swatch" + (skin.id === device.skin ? " on" : "") + (skin.owned ? "" : " locked"),
          type: "button",
          style: `--c:${skin.swatch}`,
          title: skinLabel(id, skin),
          "aria-label": skinLabel(id, skin),
          "aria-pressed": String(skin.id === device.skin),
          onclick: () => {
            this.mixing = null;
            if (!skin.owned) track("colour.tried", { device: id });
            this.actions.skin(id, skin.id);
          },
        })),
      EXTRA_DEVICE_COLOURS_VISIBLE ? h("button", {
        class: "swatch own" + (own ? " on" : "") + (member ? "" : " locked") + (device.color ? " mixed" : ""),
        type: "button",
        style: device.color ? `--c:${device.color}` : "",
        title: member ? t("room.colour.own") : t("room.colour.own.locked"),
        "aria-label": member ? t("room.colour.own") : t("room.colour.own.locked"),
        "aria-pressed": String(own),
        "aria-expanded": String(open),
        onclick: () => {
          // The server answers a non-member with what the membership gives.
          if (!member) {
            track("colour.tried", { device: id });
            return this.actions.skin(id, CUSTOM_SKIN, device.color ?? worn?.swatch ?? "#ffffff");
          }
          this.mixing = open ? null : id;
          // A colour mixed before is worn again as the palette opens.
          if (!open && device.color && !own) this.actions.skin(id, CUSTOM_SKIN, device.color);
          else this.render(core.state);
        },
      }) : null);
    if (!open) return row;
    return h("div", { class: "colours" }, row, createPalette({
      color: own && device.color ? device.color : device.color ?? worn?.swatch ?? "#ffffff",
      subject: deviceName(id),
      onPreview: (color) => this.actions.previewColor(id, color),
      onCommit: (color) => this.actions.skin(id, CUSTOM_SKIN, color),
    }));
  }

  /** The closet: every device's colours, changed while the shelf is in view. */
  private closetCard(core: StudioCore): void {
    this.open("closet",
      h("h2", { text: t("room.closet") }),
      h("p", { class: "lead", text: t("closet.lead") }),
      ...core.state.snapshot.devices.map((device) =>
        h("section", { class: "wear" },
          h("h3", { text: deviceName(device.id) }),
          this.swatches(core, device.id),
          h("small", { text: device.skin === CUSTOM_SKIN ? t("closet.own", { color: device.color ?? "" }) : ((skin) => (skin ? skinName(device.id, skin) : ""))(device.skins.find((skin) => skin.id === device.skin)) }))));
  }

  private deviceCard(core: StudioCore, id: DeviceId): void {
    const device = core.device(id);
    const apps = core.appsFor(id);
    const list = h("div", { class: "rows" }, ...apps.map((app) => this.row(app, id,
      h("button", { class: "row-main", type: "button", onclick: () => this.actions.play(app.id, id) },
        this.cover(app),
        h("span", { class: "row-text" }, h("b", { text: app.title }), h("small", { text: appTagline(app) || app.author })),
        h("span", { class: "go", text: t("room.play") })))));
    this.open("device",
      h("h2", { text: deviceName(id) }),
      this.swatches(core, id),
      apps.length
        ? h("p", { class: "lead", text: t("device.lead") })
        : h("p", { class: "lead", text: t("device.empty") }),
      list);
  }

  private appCard(core: StudioCore, id: string): void {
    const app = core.app(id);
    if (!app) return;
    const devices = core.devicesFor(id);
    const mine = app.origin === "mine";
    // Another author's game seen from its link or on Pocket TV: the visitor chooses whether it goes on the shelf.
    const offShelf = !mine && app.origin !== "starter" && !core.onShelf(id);
    // A starter is the Studio's own game, so nobody needs telling who stands behind it.
    const verified = app.verified && app.origin !== "starter";
    this.open("app",
      mine && this.renaming === app.id ? this.titleForm(core, app) : h("h2", { text: app.title }),
      h("p", { class: "lead" }, ...phrase(appTagline(app) || t("room.by", { author: app.author }))),
      verified ? h("p", { class: "note verified", text: t("room.verified") }) : null,
      app.remixOf ? h("p", { class: "note verified", text: t("room.remixOf", { title: app.remixOf.title, author: app.remixOf.author }) }) : null,
      app.kind === "site" ? this.siteRows(app) : this.pocketRows(core, app),
      offShelf ? this.keepOffer(app) : null,
      // Where a package comes from, for a visitor about to download one. The author reads theirs under Sharing.
      !mine && !app.verified && app.packages.length ? h("p", { class: "note", text: t("room.homebrew") }) : null,
      this.installLink(app),
      mine ? this.sharing(core, app) : app.url ? this.address(app.url) : null,
      app.kept ? h("button", { class: "quiet start", type: "button", onclick: () => this.actions.keep(app.id, false) }, t("room.unkeep")) : null);
  }

  /** A game off the shelf: one button puts it there, where it stays until it is taken off. */
  private keepOffer(app: AppInfo): HTMLElement {
    return h("section", { class: "step keep" },
      h("button", { class: "btn", type: "button", onclick: () => this.actions.keep(app.id, true) }, t("room.keep")),
      h("small", { text: t("room.keep.note") }));
  }

  /** Under a game's package rows: the install guide, on the web in a tab of its own. */
  private installLink(app: AppInfo): HTMLElement | null {
    if (!app.packages.length) return null;
    return h("p", { class: "note" }, h("a", { href: localized(HELP_PATH), target: "_blank", rel: "noopener", text: t("room.howToInstall") }));
  }

  /**
   * The rows of a game built outside Pocket Studio. It has no build a device
   * in the room can run. A verified one with a web build opens in the browser
   * at its own address; the address of any other shows no game to play, so
   * it gets no such row. Each package its author uploaded is a row to
   * download.
   */
  private siteRows(app: AppInfo): HTMLElement {
    const rows: HTMLElement[] = [];
    if (app.url && app.site && app.verified) {
      rows.push(h("div", { class: "row" },
        h("a", { class: "row-main", href: localized(app.url), target: "_blank", rel: "noopener" },
          h("span", { class: "row-text" }, h("b", { text: t("room.site.play") })),
          h("span", { class: "go", text: t("room.open") }))));
    }
    for (const entry of app.packages) rows.push(this.packageRow(app, entry));
    if (rows.length === 0) {
      return h("p", { class: "lead", text: app.verified ? t("room.site.empty.verified") : t("room.site.empty") });
    }
    return h("div", { class: "rows" }, ...rows);
  }

  /**
   * The rows of a Pocket app: one for each device it runs on in the room,
   * with that device's package beside it when there is one, then one for
   * each package that has no such row.
   */
  private pocketRows(core: StudioCore, app: AppInfo): HTMLElement | null {
    const devices = core.devicesFor(app.id);
    const rows = devices.map((device) => this.row(app, device.id,
      h("button", { class: "row-main", type: "button", onclick: () => this.actions.play(app.id, device.id) },
        h("span", { class: "row-text" }, h("b", { text: t("room.playOn", { device: deviceName(device.id) }) })),
        h("span", { class: "go", text: t("room.play") }))));
    for (const entry of app.packages) {
      if (!devices.some((device) => device.id === entry.target)) rows.push(this.packageRow(app, entry));
    }
    if (rows.length) return h("div", { class: "rows" }, ...rows);
    return app.origin === "mine" ? h("p", { class: "lead", text: t("room.noBuild") }) : null;
  }

  /** A game's own address, shown without its scheme, with a button that copies the whole of it. */
  private address(url: string): HTMLElement {
    const copy = h("button", {
      class: "copy", type: "button", text: t("room.copy"),
      onclick: async () => {
        track("share.copied");
        await navigator.clipboard?.writeText(url).catch(() => null);
        copy.textContent = t("room.copied");
        setTimeout(() => (copy.textContent = t("room.copy")), 1600);
      },
    });
    return h("div", { class: "command address" }, h("a", { href: localized(url), target: "_blank", rel: "noopener", text: url.replace(/^https?:\/\//, "") }), copy);
  }

  /**
   * What the author decides about one of their own games: whether it is out
   * in the world, at which address, and whether members may download it.
   */
  private sharing(core: StudioCore, app: AppInfo): HTMLElement {
    if (app.takedown) {
      // Pocket Nexus took it down: no Publish to press. What an editor wrote, and where to answer.
      return h("section", { class: "step takedown", role: "status" },
        h("h3", { text: t("room.takedown") }),
        h("small", { class: "reason", text: app.takedown.reason }),
        h("small", {}, ...rich("room.takedown.body", { email: h("a", { href: `mailto:${LEGAL.contactEmail}`, text: LEGAL.contactEmail }) })),
        this.removal(core, app));
    }
    if (app.status !== "published") {
      return h("section", { class: "step" },
        h("button", { class: "btn ghost", type: "button", onclick: () => this.actions.publish(app.id, true) }, t("room.publish")),
        h("small", { text: t("room.publish.note") }),
        this.removal(core, app));
    }
    const section = h("section", { class: "step" });
    if (app.url && app.slug) {
      const line = this.address(app.url);
      const change = h("button", { class: "quiet start", type: "button", onclick: () => {
        const input = h("input", { class: "field slug", type: "text", maxlength: "32", spellcheck: "false", autocapitalize: "off", autocomplete: "off", "aria-label": t("room.address") });
        input.value = app.slug!;
        for (const type of ["keydown", "keyup"] as const) input.addEventListener(type, (event) => event.stopPropagation());
        const note = h("p", { class: "signin-note", role: "alert" });
        const form = h("form", { class: "rename" }, input, h("button", { class: "copy", type: "submit", text: t("room.save") }));
        form.addEventListener("submit", (event) => {
          event.preventDefault();
          const next = input.value.trim().toLowerCase();
          if (next === app.slug) return this.render(core.state);
          void this.actions.rename(app.id, next).then((refusal) => {
            if (refusal) note.textContent = refusal;
          });
        });
        line.replaceWith(form);
        change.replaceWith(note);
        input.focus();
        input.select();
      } }, t("room.address.change"));
      section.append(line, change);
    } else {
      // A guest's game has a link through the room, and gets an address of its own with an account.
      section.append(this.address(`${location.origin}/studio/?app=${encodeURIComponent(app.id)}`));
    }
    const packages = app.packages;
    if (packages.length) {
      const names = packages.map((entry) => deviceName(entry.target));
      const toggle = h("button", {
        class: "switch" + (app.allowNative ? " on" : ""), type: "button", role: "switch", "aria-checked": String(!!app.allowNative),
        onclick: () => this.actions.allowNative(app.id, !app.allowNative),
      }, h("span", { text: t("room.members.download") }), h("i", { "aria-hidden": "true" }));
      section.append(toggle, h("small", {}, ...phrase(t("room.packages", { list: formatList(lang(), names) }))));
      if (!app.verified) section.append(h("small", { text: t("room.homebrew.own") }));
    }
    if (MEMBERSHIP_SIGNUPS_OPEN && !app.discoverable && !core.state.snapshot.membership.active) {
      section.append(h("small", {}, t("room.notDiscoverable"), " ",
        h("button", { class: "inline", type: "button", onclick: () => this.paywall("member") }, t("account.join"))));
    }
    section.append(
      h("button", { class: "quiet start", type: "button", onclick: () => this.actions.publish(app.id, false) }, t("room.unpublish")),
      this.removal(core, app));
    return section;
  }

  /**
   * Deleting a game: one quiet button, and in its place a second step before
   * anything is deleted. The question stays on the card through a refresh
   * and leaves with the card.
   */
  private removal(core: StudioCore, app: AppInfo): HTMLElement {
    if (this.removing !== app.id) {
      return h("div", { class: "quiets" },
        h("button", {
          class: "quiet start", type: "button",
          onclick: () => {
            this.renaming = app.id;
            this.render(core.state);
            const field = this.card.querySelector<HTMLInputElement>(".retitle input");
            field?.focus();
            field?.select();
          },
        }, t("room.rename")),
        h("button", {
          class: "quiet start", type: "button",
          onclick: () => {
            this.removing = app.id;
            this.render(core.state);
            // The answer that changes nothing is the one under the keyboard.
            this.card.querySelector<HTMLButtonElement>(".removal .keep")?.focus();
          },
        }, t("room.delete")));
    }
    return h("div", { class: "pair removal" },
      h("button", {
        class: "btn ghost", type: "button",
        onclick: (event: Event) => {
          (event.currentTarget as HTMLButtonElement).disabled = true;
          this.actions.remove(app.id);
        },
      }, t("room.delete.confirm")),
      h("button", {
        class: "btn ghost keep", type: "button",
        onclick: () => {
          this.removing = null;
          this.render(core.state);
        },
      }, t("room.delete.keep")));
  }

  private playCard(core: StudioCore, device: DeviceId, id: string): void {
    const app = core.app(id);
    const info = core.device(device);
    // The same four face-button bits carry each maker's own names.
    const face = device === "3ds" ? ["A", "B", "Y / X"] : [t("keys.circle"), t("keys.cross"), t("keys.squareTriangle")];
    const keys = info.input.buttons
      ? h("dl", { class: "keys" },
        h("dt", { text: t("keys.arrows") }), h("dd", { text: t("keys.dpad") }),
        h("dt", { text: "Z / Enter" }), h("dd", { text: face[0] }),
        h("dt", { text: "X" }), h("dd", { text: face[1] }),
        h("dt", { text: "A / S" }), h("dd", { text: face[2] }),
        h("dt", { text: "Q / E" }), h("dd", { text: "L / R" }),
        h("dt", { text: t("keys.space") }), h("dd", { text: "START" }))
      : null;
    this.open("play",
      h("h2", { text: app?.title ?? t("play.title") }),
      h("p", { class: "lead", text: info.input.touch ? (info.input.buttons ? t("play.lead.both") : t("play.lead.touch")) : t("play.lead.keys") }),
      keys);
  }

  /**
   * A game's title as a field, in place of the heading, for its author. The
   * room's name for the game is what changes: the title screen is drawn by
   * the project, which its author renames there.
   */
  private titleForm(core: StudioCore, app: AppInfo): HTMLElement {
    const input = h("input", { class: "field", type: "text", maxlength: "60", autocomplete: "off", "aria-label": t("room.title") });
    input.value = app.title;
    for (const type of ["keydown", "keyup"] as const) input.addEventListener(type, (event) => event.stopPropagation());
    const note = h("p", { class: "signin-note", role: "alert" });
    const close = () => {
      this.renaming = null;
      this.render(core.state);
    };
    const form = h("form", { class: "rename retitle" }, input, h("button", { class: "copy", type: "submit", text: t("room.save") }));
    input.addEventListener("keydown", (event) => {
      if (event.key === "Escape") close();
    });
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const next = input.value.trim();
      if (next === app.title || next === "") return close();
      void this.actions.retitle(app.id, next).then((refusal) => {
        if (refusal) note.textContent = refusal;
        else close();
      });
    });
    return h("div", { class: "retitle-box" }, form, note);
  }

  private laptopCard(): void {
    this.card.hidden = true;
    this.card.replaceChildren();
  }

  /** The television's remote: change the game, open it, quiet the announcer. */
  private tvCard(): void {
    const muted = this.actions.tvMuted();
    this.open("tv",
      h("h2", { text: "Pocket TV" }),
      h("p", { class: "lead", text: t("tv.lead") }),
      h("div", { class: "pair" },
        h("button", { class: "btn ghost", type: "button", onclick: () => this.actions.tvTurn(-1) }, t("tv.previous")),
        h("button", { class: "btn ghost", type: "button", onclick: () => this.actions.tvTurn(1) }, t("tv.next"))),
      h("button", { class: "btn", type: "button", onclick: () => this.actions.tvChoose() }, t("tv.open")),
      h("button", { class: "btn ghost", type: "button", "aria-pressed": String(!muted), onclick: () => this.actions.tvMute(!muted) },
        muted ? t("tv.soundOn") : t("tv.soundOff")),
      h("dl", { class: "keys" },
        h("dt", { text: t("keys.leftRight") }), h("dd", { text: t("tv.change") }),
        h("dt", { text: "Enter" }), h("dd", { text: t("tv.openIt") })));
  }

  /**
   * The sign-in card for a guest who reached for something an account alone
   * may have, a game's package: `lead` says what. Signed in, the room loads
   * again as the account's, and the guest presses once more.
   */
  askFor(lead: string, why: "download"): void {
    openSignIn(this.root, this.actions.account, lead, () => location.reload(), why);
  }

  /**
   * The gate shown over the room until someone is signed in. `onGuest` comes
   * in without an account; `onDone` runs after a sign-in with one.
   */
  signIn(options: { providers: AuthProvidersResponse; onGuest: () => Promise<void>; onDone: () => void }): HTMLElement {
    const gate = h("div", { class: "gate" }, createSignIn({
      providers: options.providers,
      title: t("gate.title"),
      lead: t("gate.lead"),
      why: "gate",
      onGuest: options.onGuest,
      onDone: options.onDone,
    }));
    this.root.append(gate);
    return gate;
  }
}
